import {
  DayPlanDtoSchema,
  PatientAdditionalTaskDtoSchema,
  PatientContactDtoSchema,
  PatientTaskDtoSchema,
  PatientVisitDtoSchema,
  ProcedureWorkLineDtoSchema,
  TrialAdditionalTaskDtoSchema,
  type AddOutsidePatternProcedureRequest,
  type ClosePatientTaskRequest,
  type CreatePatientContactRequest,
  type CreatePatientTaskRequest,
  type DayPlanDto,
  type DayPlanQuery,
  type PatientAdditionalTaskDto,
  type PatientContactDto,
  type PatientTaskDto,
  type PatientTasksQuery,
  type PatientVisitDto,
  type ProcedureWorkLineDto,
  type RegisterVisitRequest,
  type SaveProcedureDraftRequest,
  type SaveVisitPersonnelRequest,
  type ScheduleVisitRequest,
  type TrialAdditionalTaskDto,
  VisitPersonnelResponseSchema,
  type VisitPersonnelResponse,
} from '@ctms/contracts';

import { z } from 'zod';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

const ProcedureWorkListSchema = z.array(ProcedureWorkLineDtoSchema);
const ProcedureCatalogueSchema = z.array(TrialAdditionalTaskDtoSchema);
const PatientTasksListSchema = z.array(PatientTaskDtoSchema);

/**
 * Domain 4 read of the coordinator cockpit — the day plan of one trial
 * (REQ-VIS-002). `trialId` and `date` are required by the contract, so a plan
 * is always asked for a concrete day of a concrete trial.
 */
export const visitsApi = {
  async listDayPlan(query: DayPlanQuery): Promise<DayPlanDto> {
    const searchParams = new URLSearchParams({
      trialId: String(query.trialId),
      date: query.date,
      page: String(query.page),
      limit: String(query.limit),
    });
    if (query.status) {
      searchParams.set('status', query.status);
    }
    if (query.search) {
      searchParams.set('search', query.search);
    }
    const raw: unknown = await apiClient
      .get(API_PATHS.visits.dayPlan, { searchParams })
      .json();
    return DayPlanDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-015: books the PLANNED DAY of one patient visit. The date is a
   * calendar day (`YYYY-MM-DD`), never an instant — it is the `date` column, and
   * an explicit `null` clears the booking. The response is the visit as the
   * server stored it, so the caller re-renders from one source of truth.
   */
  async scheduleVisit(
    visitId: number,
    date: string | null,
    time?: string | null,
  ): Promise<PatientVisitDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.visits.schedule(visitId), {
        json: {
          date,
          time: date === null ? null : (time ?? null),
        } satisfies ScheduleVisitRequest,
      })
      .json();
    return PatientVisitDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-020/025/026/028: registers the PERFORMED side of one visit. `date`
   * is a calendar day; an explicit `null` undoes the registration. The response
   * is the visit as the server stored it.
   */
  async registerVisit(visitId: number, request: RegisterVisitRequest): Promise<PatientVisitDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.visits.registration(visitId), { json: request })
      .json();
    return PatientVisitDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-045: the WORKING LIST of one visit — the saved executions joined
   * with the visit pattern, deduplicated, ready for the checkbox tree. The
   * `key: null` lines are pattern positions that are not saved yet.
   */
  async getProcedureWorkList(patientVisitId: number): Promise<ProcedureWorkLineDto[]> {
    const searchParams = new URLSearchParams({ patientVisitId: String(patientVisitId) });
    const raw: unknown = await apiClient
      .get(API_PATHS.visits.procedureWorkList, { searchParams })
      .json();
    return ProcedureWorkListSchema.parse(raw);
  },

  /**
   * REQ-VIS-045 step 6 + REQ-VIS-049: persists the whole working list of one
   * visit. The response is the canonical list AFTER the save, so the caller
   * re-renders from one source of truth. Saving never deletes a line the draft
   * does not carry.
   */
  async saveProcedureDraft(
    visitId: number,
    request: SaveProcedureDraftRequest,
  ): Promise<ProcedureWorkLineDto[]> {
    const raw: unknown = await apiClient
      .put(API_PATHS.visits.procedureDraft(visitId), { json: request })
      .json();
    return ProcedureWorkListSchema.parse(raw);
  },

  /**
   * REQ-VIS-040 / REQ-VIS-050: the whole catalogue of additional procedures of
   * one trial — the source list of "add outside the pattern". A free procedure
   * (`trialVisitId: null`) belongs to the catalogue, not to a visit pattern.
   */
  async listProcedureCatalogue(trialId: number): Promise<TrialAdditionalTaskDto[]> {
    const searchParams = new URLSearchParams({ trialId: String(trialId) });
    const raw: unknown = await apiClient
      .get(API_PATHS.visits.procedureCatalogue, { searchParams })
      .json();
    return ProcedureCatalogueSchema.parse(raw);
  },

  /**
   * REQ-VIS-044 / REQ-VIS-049: ticks or unticks ONE already-saved execution.
   * The procedure date is computed by the server from the visit's own dates —
   * the client never sends it; unticking keeps the row instead of deleting it.
   */
  async setProcedureExecutionDone(
    executionId: number,
    done: boolean,
  ): Promise<PatientAdditionalTaskDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.visits.procedureExecution(executionId), { json: { done } })
      .json();
    return PatientAdditionalTaskDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-050: adds a catalogue procedure OUTSIDE the visit pattern. The row
   * is born outside the pattern, which is the only kind REQ-VIS-048 releases
   * for deletion.
   */
  async addOutsidePatternProcedure(
    request: AddOutsidePatternProcedureRequest,
  ): Promise<PatientAdditionalTaskDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.visits.procedureExecutions, { json: request })
      .json();
    return PatientAdditionalTaskDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-048: permanently removes ONLY a position added outside the pattern.
   * A pattern position is never deleted — it is unticked instead (REQ-VIS-049).
   */
  async removeProcedureExecution(executionId: number): Promise<void> {
    await apiClient.delete(API_PATHS.visits.procedureExecution(executionId));
  },

  /**
   * REQ-VIS-008: the OPEN tasks of one participation. A closed task (`stopDate`
   * set) never travels on this list, so the cockpit shows only what is to do.
   */
  async listOpenPatientTasks(query: PatientTasksQuery): Promise<PatientTaskDto[]> {
    const searchParams = new URLSearchParams({ enrollmentId: String(query.enrollmentId) });
    const raw: unknown = await apiClient
      .get(API_PATHS.visits.patientTasks, { searchParams })
      .json();
    return PatientTasksListSchema.parse(raw);
  },

  /**
   * REQ-VIS-051: opens a task. The server stamps `startDate` with its TODAY and
   * returns the row as stored, so the caller re-renders from one source of truth.
   */
  async createPatientTask(request: CreatePatientTaskRequest): Promise<PatientTaskDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.visits.patientTasks, { json: request })
      .json();
    return PatientTaskDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-051: closes ONE task. The server stamps `stopDate` with its TODAY;
   * a task outside the caller's site answers 404 (never a quiet write).
   */
  async closePatientTask(taskId: number, request: ClosePatientTaskRequest): Promise<PatientTaskDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.visits.patientTaskClose(taskId), { json: request })
      .json();
    return PatientTaskDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-038: records a contact with a patient. The moment (`contactedAt`)
   * is the server's now; the visit link is optional — a contact outside any
   * visit is allowed.
   */
  async createPatientContact(request: CreatePatientContactRequest): Promise<PatientContactDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.visits.patientContacts, { json: request })
      .json();
    return PatientContactDtoSchema.parse(raw);
  },

  /**
   * REQ-VIS-027 (#32): the personnel registered at one visit — the separate
   * "who performed it" register, expanded with roles and names for the card.
   */
  async getVisitPersonnel(visitId: number): Promise<VisitPersonnelResponse> {
    const raw: unknown = await apiClient.get(API_PATHS.visits.personnel(visitId)).json();
    return VisitPersonnelResponseSchema.parse(raw);
  },

  /**
   * REQ-VIS-027/028 (#32): replaces the personnel register of one visit in
   * full; an empty list clears it (registration without personnel is allowed).
   */
  async saveVisitPersonnel(
    visitId: number,
    payload: SaveVisitPersonnelRequest,
  ): Promise<VisitPersonnelResponse> {
    const raw: unknown = await apiClient
      .put(API_PATHS.visits.personnel(visitId), { json: payload })
      .json();
    return VisitPersonnelResponseSchema.parse(raw);
  },
};